import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { Check, Star, Loader2, GraduationCap, Building2, User } from "lucide-react";

const Pricing = () => {
  const { t, user, loginWithGoogle } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [interval, setIntervalState] = useState("month");
  const [loadingPlan, setLoadingPlan] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data || [])).catch(() => {});
  }, []);

  const meta = {
    academy_agent: {
      icon: GraduationCap,
      popular: true,
      features: ["Full player search & filters", "View videos & stats", "Watchlists & private notes", "Contact requests", "7-day free trial"],
    },
    pro_team: {
      icon: Building2,
      popular: false,
      features: ["Everything in Academy/Agent", "Multiple team seats", "Team dashboard & shared lists", "Priority support", "7-day free trial"],
    },
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast({ description: t.pricing.loginRequired });
      return loginWithGoogle();
    }
    if (!["player", "academy_agent", "pro_team"].includes(user.role)) {
      return navigate("/onboarding");
    }
    setLoadingPlan(plan.id);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan_id: plan.id,
        interval,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else throw new Error("no url");
    } catch {
      setLoadingPlan(null);
      toast({ description: t.payment.failed });
    }
  };

  return (
    <div className="pt-16 min-h-screen">
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-3xl mx-auto px-5 py-16 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white">{t.pricing.title}</h1>
          <p className="text-gray-400 mt-4 text-lg">{t.pricing.subtitle}</p>

          {/* Billing toggle */}
          <div className="inline-flex items-center bg-[#0f1116] border border-white/10 rounded-full p-1 mt-8">
            <button
              onClick={() => setIntervalState("month")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${interval === "month" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"}`}
            >
              {t.sub.monthly}
            </button>
            <button
              onClick={() => setIntervalState("year")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${interval === "year" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"}`}
            >
              {t.sub.annual}
              <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">{t.sub.save}</span>
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {plans.map((plan) => {
            const m = meta[plan.id] || { icon: User, features: [] };
            const price = interval === "month" ? plan.month : plan.year;
            const isCurrent = user?.subscription?.plan === plan.id && ["trialing", "active"].includes(user?.subscription?.status);
            return (
              <div key={plan.id} className={`relative bg-[#0f1116] border rounded-2xl p-7 flex flex-col ${m.popular ? "border-blue-500/50 shadow-lg shadow-blue-500/10" : "border-white/8"}`}>
                {m.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-wide bg-blue-600 text-white px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" /> {t.pricing.mostPopular}
                  </span>
                )}
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                  <m.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-display text-xl font-bold text-white">{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold text-white">€{price}</span>
                  <span className="text-gray-500 text-sm">{interval === "month" ? t.sub.perMonth : t.sub.perYear}</span>
                </div>
                <span className="inline-block mt-3 text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full w-fit">
                  {t.sub.trialBadge}
                </span>

                <ul className="mt-6 space-y-3 flex-1">
                  {m.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <span className="text-gray-300">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan)}
                  disabled={isCurrent || loadingPlan === plan.id}
                  className={`mt-6 w-full h-11 rounded-xl font-medium ${m.popular ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-white/5 hover:bg-white/10 text-white border border-white/10"} ${isCurrent ? "opacity-60" : ""}`}
                >
                  {loadingPlan === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? t.pricing.current : t.sub.startTrial}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Free players tier */}
        <div className="mt-8 bg-gradient-to-br from-blue-500/10 to-[#0f1116] border border-blue-500/20 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
            <User className="w-6 h-6 text-blue-400" />
          </div>
          <h3 className="font-display text-2xl font-bold text-white">{t.pricing.freeTitle}</h3>
          <p className="text-gray-400 mt-3 max-w-2xl mx-auto">{t.pricing.freeText}</p>
          <Button onClick={() => (user ? navigate("/my-profile") : loginWithGoogle())} className="mt-6 bg-blue-600 hover:bg-blue-500 text-white rounded-full h-11 px-7">
            {t.pricing.freeCta}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
